import posthogJs from 'posthog-js';
import type {CLSAttribution, INPAttribution, LCPAttribution, MetricWithAttribution} from 'web-vitals';
import {onCLS, onINP, onLCP, onFCP, onTTFB} from 'web-vitals/attribution';
import {isProjectViewEnabled} from './allspice.ts';

const {username, posthog_key, route} = (window.telemetry || {});

// Collect per page metadata that we should always send along
const pageMeta = {
  project_view: String(isProjectViewEnabled()),
};

// Replace all current_url and pathname functions with the parameterized route.
function redactObject(o: Record<string, unknown>) {
  if (o['$current_url']) {
    o['$current_url'] = window.location.origin + route;
  }
  if (o['$pathname']) {
    o['$pathname'] = route;
  }
  return o;
}

// Wrapper around navigator.sendBeacon for Hub telemetry
function sendTelemetry(type: string, data: Record<string, unknown>): void {
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${window.config.appSubUrl}/_/telemetry`, JSON.stringify({
        type,
        data,
      }));
    }
  } catch (e) {
    console.error(e);
  }
}

// Wrapper around posthog custom events
function sendPosthog(name: string, meta: Record<string, unknown>): void {
  try {
    if (posthog_key) {
      posthogJs.capture(name, meta);
    }
  } catch (e) {
    console.error(e);
  }
}

if (posthog_key) {
  posthogJs.init(posthog_key, {
    api_host: 'https://us.i.posthog.com',
    // Alter the event to include the route instead of the raw path
    // This does a bit of customer data redaction, but also help us
    // standardize flows across different customers.
    before_send: (event) => {
      if (!event) {
        return null;
      }

      const redactedProperties = redactObject(event.properties || {});
      event.properties = redactedProperties;
      if (event.event === '$$heatmap') {
          // $heatmap data is keyed by url
        event.properties.$heatmap_data = redactObject(event.properties.$heatmap_data || {});
      }

      const redactedSet = redactObject(event.$set || {});
      event.$set = redactedSet;

      const redactedSetOnce = redactObject(event.$set_once || {});
      event.$set_once = redactedSetOnce;

      return event;
    },
  });

  if (username) {
    // Since Posthog may be used across multiple domains, add an @<domain> to each user.
    const uniqueUser = `${username}@${window.location.hostname}`;
    posthogJs.identify(uniqueUser, {
      username,
      'instance': window.location.hostname,
    });
  } else {
    // If there isn't a username, the user has logged out, and we should reset it.
    posthogJs.reset();
  }
}

class WebVitals {
  metrics: Record<string, unknown> | null;
  start: number;

  constructor() {
    this.reset();
  }

  reset() {
    const newMetrics: Record<string, unknown> = {
      path: window.location.pathname,
      route,
    };

    if (this.metrics) {
      newMetrics.navigationType = this.metrics.navigationType;
    }

    this.metrics = newMetrics;
    this.start = performance.now();
  }

  log({name, navigationType, rating, value, id, attribution}: MetricWithAttribution) {
    console.debug('web_vitals', name, rating, value);
    this.metrics.navigationType = navigationType;

    const params: Record<string, unknown> = {
      id,
      rating,
      value: Math.round(value),
    };

    switch (name) {
      case 'CLS':
        params.target = (attribution as CLSAttribution).largestShiftTarget;
        break;
      case 'INP':
        params.target = (attribution as INPAttribution).interactionTarget;
        break;
      case 'LCP':
        params.target = (attribution as LCPAttribution).element;
        break;
    }

    this.metrics[name] = params;
  }

  handleVisibility() {
    // Report all available metrics whenever the page is backgrounded or unloaded.
    if (document.visibilityState === 'hidden') {
      const metrics = this.metrics;
      metrics.sessionTime = Math.round(performance.now() - this.start);
      metrics.meta = pageMeta;

      // Only send web_vitals to backend, since Posthog implements it themselves
      sendTelemetry('web_vitals', metrics);
    } else {
      // start a new session
      this.reset();
    }
  }

  listen() {
    onCLS(this.log.bind(this));
    onINP(this.log.bind(this));
    onLCP(this.log.bind(this));
    onFCP(this.log.bind(this));
    onTTFB(this.log.bind(this));

    window.addEventListener('visibilitychange', this.handleVisibility.bind(this));
  }
}

const vitals = new WebVitals();
vitals.listen();

export function event(name: string, meta: Record<string, unknown> = {}) {
  try {
    const params = {
      event: name,
      path: window.location.pathname,
      route,
      meta: {...meta, ...pageMeta},
    };

    console.debug('event', params);
    sendTelemetry('event', params);
    sendPosthog(name, meta);
  } catch (e) {
    console.error(e);
  }
}

export function timing(name: string, duration: number, meta: Record<string, unknown> = {}) {
  try {
    const params = {
      event: name,
      path: window.location.pathname,
      route,
      dur: Math.round(duration),
      meta: {...meta, ...pageMeta},
    };

    // Add a measure so that this shows up in frontend profiling
    performance.measure(name, {
      duration,
      end: performance.now(),
    });
    console.debug('timing', params);
    sendTelemetry('event', params);
    sendPosthog(name, {dur: duration, ...meta});
  } catch (e) {
    console.error(e);
  }
}
